import {useCallback, useEffect, useRef, useState} from "react";
import apiClient from "../utils/apiClient";
import {ACTIONS, RESOURCES, useAuthPermissions} from "./useAuthPermissions";

const KPI_GROUPS = [
  {
    key: "users",
    title: "Usuarios",
    url: "/users",
    resource: RESOURCES.USERS,
    items: [
      {key: "total", label: "Total de usuarios"},
      {key: "active", label: "Activos", params: {status: "active"}},
      {key: "inactive", label: "Inactivos", params: {status: "inactive"}},
      {key: "today", label: "Creados hoy", range: "today"},
      {key: "week", label: "Creados esta semana", range: "week"}
    ]
  },
  {
    key: "tenants",
    title: "Tenants",
    url: "/tenants",
    resource: RESOURCES.TENANTS,
    items: [
      {key: "total", label: "Total de tenants"},
      {key: "active", label: "Activos", params: {status: "active"}},
      {key: "inactive", label: "Inactivos", params: {status: "inactive"}},
      {key: "today", label: "Creados hoy", range: "today"},
      {key: "week", label: "Creados esta semana", range: "week"}
    ]
  },
  {
    key: "tickets",
    title: "Tickets",
    url: "/tickets",
    resource: RESOURCES.TICKETS,
    items: [
      {key: "total", label: "Vigentes totales"},
      {key: "registration", label: "De registro", params: {ticket_type: "registration"}},
      {key: "invitation", label: "De invitación", params: {ticket_type: "invitation"}}
    ]
  }
];

const toIsoDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const buildRanges = () => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(monday.getDate() - ((now.getDay() + 6) % 7));

  const today = toIsoDay(now);
  return {
    today: `${today},${today}`,
    week: `${toIsoDay(monday)},${today}`
  };
};

const countItems = async (url, params) => {
  const response = await apiClient.get(url, {params: {...params, limit: 1}});
  return response.data?.pagination?.totalItems ?? 0;
};

const useDashboardKpis = () => {
  const {can} = useAuthPermissions();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const canRef = useRef(can);
  useEffect(() => {
    canRef.current = can;
  });

  const load = useCallback(async () => {
    setLoading(true);
    const ranges = buildRanges();

    const loaded = await Promise.all(
      KPI_GROUPS.map(async (group) => {
        if (!canRef.current(ACTIONS.READ, group.resource)) {
          return {...group, allowed: false, items: []};
        }

        const items = await Promise.all(
          group.items.map(async (item) => {
            const params = {
              ...item.params,
              ...(item.range ? {created_at: ranges[item.range]} : {})
            };

            try {
              return {...item, value: await countItems(group.url, params)};
            } catch (error) {
              console.error(`Error cargando KPI ${group.key}.${item.key}:`, error);
              return {...item, value: null};
            }
          })
        );

        return {...group, allowed: true, items};
      })
    );

    setGroups(loaded);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {groups, loading, refresh: load};
};

export default useDashboardKpis;
